import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CustomLinkService } from "../services/customLinkService";
import { CustomLink, CreateCustomLinkRequest, UpdateCustomLinkRequest } from "../types/customLink";
import useToastStore from "../store/useToastStore";

export const useCustomLinks = () => {
    const { data: customLinks, isLoading, error, refetch } = useQuery({
        queryKey: ['custom-links'],
        queryFn: () => CustomLinkService.getAllCustomLinks(),
    });

    return {
        customLinks: customLinks || [],
        isLoading,
        error,
        refetch,
    };
};

export const useCustomLink = (id: string) => {
    const { data: customLink, isLoading, error, refetch } = useQuery({
        queryKey: ['custom-link', id],
        queryFn: () => CustomLinkService.getCustomLinkById(id),
        enabled: !!id,
    });

    return {
        customLink,
        isLoading,
        error,
        refetch,
    };
};

export const useCustomLinkBySlug = (slug: string) => {
    const { data: customLink, isLoading, error, refetch } = useQuery({
        queryKey: ['custom-link-slug', slug],
        queryFn: () => CustomLinkService.getCustomLinkBySlug(slug),
        enabled: !!slug,
    });

    return {
        customLink,
        isLoading,
        error,
        refetch,
    };
};

export const useCreateCustomLink = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToastStore();

    const mutation = useMutation({
        mutationFn: (data: CreateCustomLinkRequest) => CustomLinkService.createCustomLink(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['custom-links'] });
            showToast('Custom link created successfully!', 'success');
        },
        onError: (error) => {
            console.error('Error creating custom link:', error);
            showToast('Failed to create custom link', 'error');
        },
    });

    return {
        createCustomLink: mutation.mutate,
        isCreating: mutation.isPending,
        error: mutation.error,
    };
};

export const useUpdateCustomLink = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToastStore();

    const mutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: UpdateCustomLinkRequest }) =>
            CustomLinkService.updateCustomLink(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['custom-links'] });
            queryClient.invalidateQueries({ queryKey: ['custom-link'] });
            showToast('Custom link updated successfully!', 'success');
        },
        onError: (error) => {
            console.error('Error updating custom link:', error);
            showToast('Failed to update custom link', 'error');
        },
    });

    return {
        updateCustomLink: mutation.mutate,
        isUpdating: mutation.isPending,
        error: mutation.error,
    };
};

export const useDeleteCustomLink = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToastStore();

    const mutation = useMutation({
        mutationFn: (id: string) => CustomLinkService.deleteCustomLink(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['custom-links'] });
            showToast('Custom link deleted successfully!', 'success');
        },
        onError: (error) => {
            console.error('Error deleting custom link:', error);
            showToast('Failed to delete custom link', 'error');
        },
    });

    return {
        deleteCustomLink: mutation.mutate,
        isDeleting: mutation.isPending,
        error: mutation.error,
    };
};

export const useAddProductsToLink = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToastStore();

    const mutation = useMutation({
        mutationFn: ({ id, productIds }: { id: string; productIds: string[] }) =>
            CustomLinkService.addProductsToLink(id, productIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['custom-links'] });
            queryClient.invalidateQueries({ queryKey: ['custom-link'] });
            showToast('Products added to link successfully!', 'success');
        },
        onError: (error) => {
            console.error('Error adding products to link:', error);
            showToast('Failed to add products to link', 'error');
        },
    });

    return {
        addProductsToLink: mutation.mutate,
        isAdding: mutation.isPending,
        error: mutation.error,
    };
};

export const useRemoveProductsFromLink = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToastStore();

    const mutation = useMutation({
        mutationFn: ({ id, productIds }: { id: string; productIds: string[] }) =>
            CustomLinkService.removeProductsFromLink(id, productIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['custom-links'] });
            queryClient.invalidateQueries({ queryKey: ['custom-link'] });
            showToast('Products removed from link successfully!', 'success');
        },
        onError: (error) => {
            console.error('Error removing products from link:', error);
            showToast('Failed to remove products from link', 'error');
        },
    });

    return {
        removeProductsFromLink: mutation.mutate,
        isRemoving: mutation.isPending,
        error: mutation.error,
    };
};

export const useToggleLinkStatus = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToastStore();

    const mutation = useMutation({
        mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
            CustomLinkService.toggleLinkStatus(id, isActive),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['custom-links'] });
            queryClient.invalidateQueries({ queryKey: ['custom-link'] });
            showToast('Link status updated successfully!', 'success');
        },
        onError: (error) => {
            console.error('Error updating link status:', error);
            showToast('Failed to update link status', 'error');
        },
    });

    return {
        toggleLinkStatus: mutation.mutate,
        isToggling: mutation.isPending,
        error: mutation.error,
    };
};
